import { ref, computed, unref, watch, type Ref } from 'vue';
import Deal from '@/models/Deal';
import Attachment from '@/models/Attachment';
import { DealStage } from '@/modules/deals/enums/DealStage';
import DealsApi from '@/modules/deals/services/DealsApi';
import DraftDealsApi from '@/modules/draftDeals/services/DraftDealsApi';
import AuthService from '@/modules/user/AuthService';
import { hasAccessToNewDealFlow } from '@/modules/user/services/Utils';
import { DealsFilter } from '@/models/Filter';
import { clone } from '@/services/utils/BasicUtils';

type MaybeRef<T> = T | Ref<T>;
// Trigger to refresh deals data
const dealsDataRefreshTrigger = ref(0);

export const triggerDealsDataRefresh = () => {
  dealsDataRefreshTrigger.value++;
};

export const useClientPreviewDealsData = (
  clientId: MaybeRef<number>,
  clientAttachments: MaybeRef<Attachment[]>,
  userId?: MaybeRef<number | undefined>,
) => {
  const dealList = ref<Nullable<Array<Deal>>>([]);
  const draftDeals = ref<Array<Deal>>([]);
  const dealsLoading = ref<boolean>(false);
  const draftDealsLoading = ref<boolean>(false);

  const getClientDeals = async () => {
    const filters = new DealsFilter();
    filters.clients = [unref(clientId)];
    filters.stages = [DealStage.PENDING];
    filters.page = 1;
    filters.rowsPerPage = 10;
    try {
      dealsLoading.value = true;
      const response = await DealsApi.filterDeals(filters);
      dealList.value = response.items;
    } catch (e) {
      console.error(e);
    } finally {
      dealsLoading.value = false;
    }
  };

  const getClientDraftDeals = async () => {
    const resolvedUserId = userId ? unref(userId) : undefined;
    if (hasAccessToNewDealFlow(AuthService.User) && resolvedUserId) {
      try {
        draftDealsLoading.value = true;
        draftDeals.value = await DraftDealsApi.getDraftDeals(unref(clientId), resolvedUserId);
      } catch (e) {
        console.error(e);
      } finally {
        draftDealsLoading.value = false;
      }
    }
  };

  const getData = async () => {
    await getClientDeals();
    await getClientDraftDeals();
  };

  watch(dealsDataRefreshTrigger, () => {
    getData();
  });

  const pendingDeals = computed(() => {
    return dealList.value ? dealList.value.filter(deal => deal.stage === DealStage.PENDING) : [];
  });

  const lastDeal = computed(() => {
    return dealList.value!
      .filter(deal => deal.stage === DealStage.PENDING)
      .sort((dealA, dealB) => new Date(dealB.updatedAt!).getTime() - new Date(dealA.updatedAt!).getTime())[0];
  });

  const lastAttachmentsList = computed(() => {
    return clone(unref(clientAttachments) ?? [])
      .sort((dealA, dealB) => new Date(dealB.createdDate!).getTime() - new Date(dealA.createdDate!).getTime())
      .filter((x, index) => index < 4);
  });

  return {
    dealList,
    draftDeals,
    dealsLoading,
    draftDealsLoading,
    pendingDeals,
    lastDeal,
    lastAttachmentsList,
    getClientDeals,
    getClientDraftDeals,
    getData,
  };
};
